import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adbApi, buildWsUrl, controlApi } from '@/lib/api';
import { useDevice } from '@/store/device';
import {
  CmdRunCard,
  type CmdRunCardHandle,
  type CmdSubEvent,
} from '@/components/CmdRunCard';
import { ControlClientPanel } from '@/components/ControlClientPanel';
import { specHasRequiredArgs } from '@/components/ControlCommandList';
import type {
  CommandSpec,
  ControlClientInfo,
  ControlHistoryItem,
  InstalledWshubApp,
} from '@/lib/types';
import {
  Activity,
  History,
  Layers,
  Loader2,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { tsLocal } from '@/lib/format';
import {
  useDevicePrefs,
  useDevicePrefsUpdate,
  useDevicePackagePrefs,
  useDevicePackagePrefsUpdate,
} from '@/lib/myPrefs';

/**
 * Per (serial, package) history of recent runs — surfaces in the right-column
 * "History" tab so QA can re-fire a previous command with a single click.
 *
 * 阶段 A：「last picked package」+ 每个 package 的 history 都迁到 B2
 * `prefs.devices[serial].selectedPkg` 和 `prefs.devices[serial].packages[pkg].control.history`，
 * 跟账号走、跨电脑同步。容量上限 50 条仍保留以避免 prefs 体积爆炸。
 */
const HISTORY_LIMIT = 50;

interface ControlPackagePrefs {
  control?: {
    history?: ControlHistoryItem[];
    pins?: unknown;
    collapsed?: unknown;
  };
}

function readHistoryFromPrefs(prefs: ControlPackagePrefs): ControlHistoryItem[] {
  const raw = prefs.control?.history;
  return Array.isArray(raw) ? (raw as ControlHistoryItem[]) : [];
}

interface ControlEvent {
  kind: 'open' | 'hello' | 'close' | 'reply' | 'evt' | 'progress' | 'unknown';
  package: string | null;
  payload: unknown;
  time: number;
}

export default function Control() {
  const { serial } = useDevice();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  /* -------------------------- Connected clients -------------------------- */
  const clientsQ = useQuery({
    queryKey: ['control.clients', serial],
    queryFn: () => controlApi.clients(serial!),
    enabled: !!serial,
    refetchInterval: 3_000,
    refetchOnWindowFocus: true,
    staleTime: 2_000,
    // Backoff-aware retry policy:
    //   - 503 (BACKOFF): the hub has already told us when to retry via
    //     Retry-After. react-query's default 3× burst would erase that
    //     hint; just let `refetchInterval` (3s) be the next attempt.
    //   - 404 (DEVICE_NOT_FOUND): retrying immediately can't bring the
    //     phone back; defer to refetchInterval / the auto-revive
    //     effect that bumps wsResetTick when the device returns.
    //   - everything else: keep one retry (transient hub blip, etc.).
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 503 || status === 404) return false;
      return failureCount < 1;
    },
  });
  const clients: ControlClientInfo[] = clientsQ.data?.clients ?? [];

  /* ---------------------- Installed-but-not-connected -------------------- */
  /**
   * Companion query to {@link clientsQ}: returns every package on the
   * device that carries the `com.adh.wshub.sdk` manifest marker, with
   * an `isConnected` flag joined from the hub's live clients map. The
   * left rail renders disconnected rows (`isConnected=false`) as a
   * sibling of the connected `ControlClientPanel`s so users see the
   * complete "apps with SDK on this device" set in one list.
   *
   * Hits the hub harder than `/control/clients` (real adb fan-out per
   * package), so a longer poll interval (30s) — Restart presses
   * invalidate the cache explicitly so the panel flips faster than
   * the poll cadence in the common case.
   */
  const installedAppsQ = useQuery({
    queryKey: ['control.installedApps', serial],
    queryFn: () => controlApi.installedApps(serial!),
    enabled: !!serial,
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 503 || status === 404) return false;
      return failureCount < 1;
    },
  });
  const installedApps: InstalledWshubApp[] = installedAppsQ.data?.apps ?? [];

  /* ----------------------------- Picked pkg ----------------------------- */
  const urlPkg = searchParams.get('pkg') || undefined;
  const persistedPkg = useDevicePrefs(serial ?? '', (d) => d.selectedPkg);
  const updateDeviceForPkg = useDevicePrefsUpdate(serial ?? '');

  const pkg: string | undefined = useMemo(() => {
    const candidates = [urlPkg, persistedPkg ?? undefined];
    for (const c of candidates) {
      if (c && clients.some((cl) => cl.package === c)) return c;
    }
    if (clients.length === 1) return clients[0].package;
    return undefined;
  }, [urlPkg, persistedPkg, clients]);

  useEffect(() => {
    if (!pkg || !serial) return;
    if (urlPkg !== pkg) {
      const next = new URLSearchParams(searchParams);
      next.set('pkg', pkg);
      setSearchParams(next, { replace: true });
    }
    if (persistedPkg !== pkg) {
      updateDeviceForPkg({ selectedPkg: pkg });
    }
  }, [pkg, urlPkg, persistedPkg, serial, searchParams, setSearchParams, updateDeviceForPkg]);

  const setPkg = useCallback(
    (next: string) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('pkg', next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  /* -------------------------------- Catalog ------------------------------ */
  /**
   * Active-pkg catalog. After the multi-panel refactor each
   * {@link ControlClientPanel} owns its own {@code useQuery} for its
   * command list — this page-level query is kept ONLY to power the
   * run card on the middle column (activeSpec lookup, dependsOn
   * resolution). Sharing the same `['control.catalog', serial, pkg]`
   * key means the active panel's query + this query dedupe at the
   * React Query cache layer — no duplicated network hit.
   */
  const cat = useQuery({
    queryKey: ['control.catalog', serial, pkg],
    queryFn: () => controlApi.catalog(serial!, pkg),
    enabled: !!serial && !!pkg,
    staleTime: 30_000,
  });

  /* --------------------- Expanded panels (left rail) --------------------- */
  /**
   * Which left-rail app panels are currently expanded. Independent of
   * the "active" pkg — users routinely browse another app's commands
   * (collapsed run card) while the active pkg's events stream stays
   * open. Defaults to the active pkg expanded; we auto-expand any pkg
   * the user picks via {@link pickCmdFor} so they always see what
   * they're driving.
   */
  const [expandedSet, setExpandedSet] = useState<Set<string>>(() =>
    pkg ? new Set([pkg]) : new Set()
  );

  useEffect(() => {
    if (!pkg) return;
    setExpandedSet((prev) => {
      if (prev.has(pkg)) return prev;
      const next = new Set(prev);
      next.add(pkg);
      return next;
    });
  }, [pkg]);

  const toggleExpanded = useCallback((p: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  }, []);

  const ensureExpanded = useCallback((p: string) => {
    setExpandedSet((prev) => {
      if (prev.has(p)) return prev;
      const next = new Set(prev);
      next.add(p);
      return next;
    });
  }, []);

  /* --------------------------- WS event stream --------------------------- */
  const [events, setEvents] = useState<ControlEvent[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const [inFlightId, setInFlightId] = useState<string | undefined>();

  /**
   * Browser ↔ hub events WS health.
   *
   * - `connecting`: dialing or scheduled for retry (covers the inter-attempt
   *   backoff window so the UI shows continuous "reconnecting" rather than
   *   alternating "closed → connecting" flicker).
   * - `open`: handshake completed; live frames are flowing.
   * - `absent`: hub says the device is gone (WS close 4404 DEVICE_NOT_FOUND).
   *   We STOP reconnecting in this state — pinging an unplugged phone
   *   every 30s achieves nothing. Auto-revives the moment
   *   `/api/control/clients` polls 200 again (the device came back AND
   *   the hub-side backoff cleared).
   * - `closed`: terminal — only entered on unmount / serial / pkg change
   *   (i.e. when our useEffect cleanup intentionally closes).
   *
   * NOT the same as {@link connected} below — that one tracks the
   * device-side SDK ↔ hub bridge (derived from `hello`/`close` frames
   * the hub sends through THIS WS).
   */
  const [wsState, setWsState] = useState<'connecting' | 'open' | 'absent' | 'closed'>(
    'connecting'
  );
  /**
   * Bump to force the useEffect below to tear down + redial. The Refresh
   * button increments this so a click yields a guaranteed fresh handshake
   * (which on the hub side also re-asserts `adb reverse` via
   * `controlManager.get(serial)` — see `handleControlEvents` in wsBridge).
   */
  const [wsResetTick, setWsResetTick] = useState(0);

  useEffect(() => {
    if (!serial || !pkg) {
      setWsState('closed');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let currentWs: WebSocket | null = null;

    /**
     * Parse the structured close reason the hub sends for typed failures.
     * `wsBridge.handleControlEvents` stringifies a `{code, kind?,
     * retryAfterMs?, attempts?}` payload into the 123-byte reason slot.
     * Returns null for plain-text reasons / non-JSON.
     */
    type CloseInfo = {
      code: 'BACKOFF' | 'DEVICE_NOT_FOUND' | 'OTHER';
      kind?: 'DEVICE_NOT_FOUND' | 'OTHER';
      retryAfterMs?: number;
      attempts?: number;
    };
    const parseReason = (reason: string): CloseInfo | null => {
      if (!reason || reason[0] !== '{') return null;
      try {
        return JSON.parse(reason) as CloseInfo;
      } catch {
        return null;
      }
    };

    const open = (): void => {
      if (cancelled) return;
      setWsState('connecting');
      const ws = new WebSocket(
        buildWsUrl('/api/control/events', { serial, package: pkg })
      );
      currentWs = ws;
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        // INTENTIONALLY do NOT reset `attempts` here. The hub sometimes
        // immediately closes a freshly-opened socket (e.g. 4503 backoff
        // because the device isn't there) — resetting `attempts` on the
        // `onopen` that happens microseconds before the close would
        // restart the 2^n ladder from zero on every retry, producing
        // the 1-second reconnect loop the user observed. We treat
        // `onmessage` (first real frame received) as the canonical
        // "we're healthy" signal instead.
        setWsState('open');
      };
      ws.onmessage = (ev) => {
        // First successful frame — confirms an end-to-end healthy
        // session, so it's safe to collapse the exponential backoff.
        if (attempts > 0) attempts = 0;
        try {
          const obj = JSON.parse(String(ev.data)) as ControlEvent;
          setEvents((prev) => [obj, ...prev].slice(0, 200));
          if (obj.kind === 'progress') {
            const id = (obj.payload as { id?: string } | null)?.id;
            if (id) setInFlightId(id);
          }
          if (obj.kind === 'reply') {
            const id = (obj.payload as { id?: string } | null)?.id;
            if (id && id === inFlightId) setInFlightId(undefined);
          }
        } catch {
          /* ignore non-json frames */
        }
      };
      ws.onerror = () => {
        // Browsers fire `error` then `close` for nearly every fault — keep
        // the reconnect path in `onclose` only so we don't schedule twice.
      };
      ws.onclose = (event) => {
        if (currentWs === ws) {
          currentWs = null;
          if (wsRef.current === ws) wsRef.current = null;
        }
        if (cancelled) return;

        // Hub-driven typed failures: 4404 = device gone (stop dialing
        // until the device comes back or user hits Reconnect), 4503 =
        // backoff (respect the server's retryAfterMs hint so SPA and
        // hub stay in lockstep instead of each running its own ladder).
        if (event.code === 4404) {
          setWsState('absent');
          // No retryTimer. An effect below watches `clientsQ`
          // success and force-redials when the device reappears.
          return;
        }
        const info = parseReason(event.reason ?? '');
        let backoffMs: number;
        if (event.code === 4503 && info && typeof info.retryAfterMs === 'number') {
          // Server already advanced its 2^n ladder for us; just sleep
          // for the hint (clamped to 30s, floored at 250ms for sanity).
          backoffMs = Math.max(250, Math.min(30_000, info.retryAfterMs));
        } else {
          // Generic exponential backoff for non-hub failures (network
          // blip, hub bounce, 1011, etc.). Base 2s, cap 30s — matches
          // the hub-side BACKOFF_BASE_MS / BACKOFF_MAX_MS in
          // src/server/controllers.ts so a recovering device's two
          // backoff ladders converge instead of fighting each other.
          backoffMs = Math.min(30_000, 2_000 * 2 ** attempts);
        }
        attempts++;
        setWsState('connecting');
        retryTimer = setTimeout(open, backoffMs);
      };
    };

    setEvents([]);
    open();

    return () => {
      cancelled = true;
      if (retryTimer != null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (currentWs) {
        // Bypass onclose so we don't schedule yet another reconnect.
        currentWs.onclose = null;
        currentWs.close();
        currentWs = null;
      }
      if (wsRef.current) wsRef.current = null;
      setWsState('closed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial, pkg, wsResetTick]);

  /**
   * Auto-revive from the `'absent'` terminal state when the device
   * reappears. The clientsQ poll (every 3s) is the canonical "device
   * is back" signal — once it returns successfully, bump
   * {@link wsResetTick} so the WS effect tears down and redials. No
   * user action required.
   */
  useEffect(() => {
    if (wsState !== 'absent') return;
    if (!clientsQ.isSuccess) return;
    setWsResetTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsState, clientsQ.isSuccess]);

  /**
   * Per-app reconnect. Triggered by the {@code RefreshCw} button on each
   * {@link ControlClientPanel}'s header.
   *
   * <ul>
   *   <li>If {@code pkgArg} is already the active pkg, this is exactly
   *       the old global "reconnect" behaviour: clear the events
   *       buffer + bump {@link wsResetTick} so the events WS effect
   *       tears down + redials (which on the hub side re-asserts
   *       {@code adb reverse} via {@code controlManager.get}).</li>
   *   <li>Otherwise, switch active to {@code pkgArg}; the events WS
   *       effect's dep array on {@code pkg} will tear down + redial
   *       automatically against the new package.</li>
   * </ul>
   *
   * In both cases we invalidate the per-(serial,pkg) catalog cache so
   * the affected panel refetches its catalog, and we refetch
   * {@code clientsQ} so a fresh `hello` window (after device hot-plug,
   * say) updates the connected-app list immediately.
   */
  const reconnectFor = useCallback(
    (pkgArg: string): void => {
      ensureExpanded(pkgArg);
      if (pkgArg === pkg) {
        // Already active: replicate the legacy global reconnect.
        setEvents([]);
        setWsResetTick((t) => t + 1);
      } else {
        // Switch active — the events WS useEffect on [serial, pkg]
        // tears down the old socket and redials against pkgArg.
        setPkg(pkgArg);
      }
      void clientsQ.refetch();
      void queryClient.invalidateQueries({
        queryKey: ['control.catalog', serial, pkgArg],
      });
    },
    [pkg, serial, setPkg, ensureExpanded, clientsQ, queryClient]
  );

  /**
   * Picking a command from a panel. Switches the active pkg if needed
   * (so the run card and events stream follow), sets the active cmd
   * name (driving the middle column), and guarantees the source panel
   * is expanded so the user can see what they picked.
   */
  const pickCmdFor = useCallback(
    (pkgArg: string, cmdName: string): void => {
      if (pkgArg !== pkg) setPkg(pkgArg);
      ensureExpanded(pkgArg);
      setActive(cmdName);
    },
    [pkg, setPkg, ensureExpanded]
  );

  /**
   * Disconnected-panel Restart: chained `app.stop` → `app.start` via
   * the existing `/api/adb/run` endpoint (inherits the hub's lease /
   * identity / visibility middleware — no new write surface). On
   * success we nudge both `clientsQ` and `installedAppsQ` so the panel
   * flips back to connected as soon as the hub sees the new hello,
   * faster than the 3s / 30s steady-state poll cadences would.
   */
  const restartFor = useCallback(
    async (pkgArg: string): Promise<void> => {
      if (!serial) return;
      await adbApi.run({ serial, name: 'app.stop', args: { package: pkgArg } });
      await adbApi.run({ serial, name: 'app.start', args: { package: pkgArg } });
      void clientsQ.refetch();
      void installedAppsQ.refetch();
    },
    [serial, clientsQ, installedAppsQ]
  );

  /* --------------------------- History + replay -------------------------- */
  const pkgPrefs = useDevicePackagePrefs<ControlPackagePrefs>(
    serial ?? '',
    pkg ?? '',
  );
  const updatePkgPrefs = useDevicePackagePrefsUpdate(serial ?? '', pkg ?? '');
  const history: ControlHistoryItem[] = useMemo(
    () => (serial && pkg ? readHistoryFromPrefs(pkgPrefs) : []),
    [serial, pkg, pkgPrefs],
  );

  const [replayArgs, setReplayArgs] = useState<unknown | undefined>();
  const replayArgsConsumedRef = useRef(false);

  const recordRun = useCallback(
    (item: ControlHistoryItem) => {
      if (!serial || !pkg) return;
      const next = [item, ...history.filter((x) => x.id !== item.id)].slice(0, HISTORY_LIMIT);
      updatePkgPrefs({ control: { history: next } });
    },
    [serial, pkg, history, updatePkgPrefs],
  );

  /* ----------------------------- Selection ----------------------------- */
  const [active, setActive] = useState<string | undefined>();
  const [rightTab, setRightTab] = useState<'events' | 'history'>('events');

  useEffect(() => {
    const cmdParam = searchParams.get('cmd');
    if (cmdParam) setActive(cmdParam);
  }, [searchParams]);

  const commands = (cat.data?.commands ?? []) as CommandSpec[];
  const commandsByName = useMemo(() => {
    const m = new Map<string, CommandSpec>();
    for (const c of commands) m.set(c.name, c);
    return m;
  }, [commands]);
  const activeSpec = active ? commandsByName.get(active) : undefined;

  /**
   * Consume a queued cross-pkg inline ▷ once the card lands on the
   * matching spec. The double check (pkg + active + activeSpec.name)
   * guards against stale entries from an aborted run sequence
   * (e.g. user switched apps mid-flight).
   *
   * <p>A {@code setTimeout} of 0 punts the run to the next macrotask
   * so the just-mounted card has a render cycle to wire up its
   * `useImperativeHandle` (which only resolves after the commit
   * phase). Without the punt, `cardRef.current` would be reliably
   * non-null in most browsers but the timing is brittle when the
   * spec also triggered a `defaultArgs` reset.
   */
  useEffect(() => {
    const pending = pendingRunRef.current;
    if (!pending) return;
    if (pending.pkg !== pkg) return;
    if (pending.cmd !== active) return;
    if (!activeSpec || activeSpec.name !== pending.cmd) return;
    pendingRunRef.current = null;
    const handle = window.setTimeout(() => {
      void cardRef.current?.run();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [pkg, active, activeSpec]);

  /* ------------ Connection + dep-satisfied tracking ------------ */
  const lastHelloOrClose = events.find(
    (e) => e.kind === 'open' || e.kind === 'hello' || e.kind === 'close'
  );
  const connected =
    !!pkg &&
    !!clients.find((c) => c.package === pkg) &&
    lastHelloOrClose?.kind !== 'close';

  /** Names of dep commands that have replied ok in this session. */
  const [satisfiedDeps, setSatisfiedDeps] = useState<Set<string>>(new Set());
  useEffect(() => {
    // Reset when device or app changes — chip state is session-scoped.
    setSatisfiedDeps(new Set());
  }, [serial, pkg]);

  /**
   * Flat evt-frame view that CmdRunCard (subscription mode) filters by
   * its active subId. We derive this from {@link events} so we don't
   * have to maintain a parallel buffer — the events log already keeps
   * the most recent 200 frames per session and the card slices on top.
   *
   * The wire envelope is `{kind:'evt', payload:{type:'evt', subId, payload, time}}`
   * (see `wsBridge.handleControlEvents`), so we unwrap one level.
   */
  const subEvents = useMemo<CmdSubEvent[]>(() => {
    const out: CmdSubEvent[] = [];
    for (const e of events) {
      if (e.kind !== 'evt') continue;
      const env = e.payload as {
        subId?: unknown;
        payload?: unknown;
        time?: unknown;
      } | null;
      if (!env || typeof env.subId !== 'string') continue;
      out.push({
        subId: env.subId,
        time: typeof env.time === 'number' ? env.time : e.time,
        payload: env.payload,
      });
    }
    return out;
  }, [events]);

  /* ------------------------------- Cancel ------------------------------- */
  const cancelInflight = useCallback(async () => {
    if (!serial || !pkg || !inFlightId) return;
    try {
      await controlApi.cancel({ serial, package: pkg, id: inFlightId, reason: 'user cancel' });
      setInFlightId(undefined);
    } catch {
      /* ignore — the reply will land as CANCELLED regardless */
    }
  }, [serial, pkg, inFlightId]);

  /* ------------------ Command dispatch helpers ------------------ */

  const runCommand = useCallback(
    async (
      cmdName: string,
      args: Record<string, unknown>,
      opts: { mode?: 'preview' | 'apply' } = {}
    ): Promise<unknown> => {
      if (!serial || !pkg) throw new Error('no serial/package');
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const startedAt = Date.now();
      const historyItem: ControlHistoryItem = {
        id,
        startedAt,
        serial,
        package: pkg,
        name: cmdName,
        args,
      };
      try {
        const r = await controlApi.cmd<{
          ok: boolean;
          result?: unknown;
          error?: { code: string; message: string };
        }>({
          serial,
          package: pkg,
          name: cmdName,
          args,
          mode: opts.mode,
        });
        // Only record successful "apply" runs in history; preview probes
        // are noise (they fire on every snapshot click).
        if (opts.mode !== 'preview') {
          recordRun({
            ...historyItem,
            durationMs: Date.now() - startedAt,
            ok: r.reply.ok,
            result: r.reply.result,
            error: r.reply.error,
          });
        }
        if (opts.mode !== 'preview' && r.reply.ok) {
          setSatisfiedDeps((prev) => {
            if (prev.has(cmdName)) return prev;
            const next = new Set(prev);
            next.add(cmdName);
            return next;
          });
        }
        return r.reply;
      } catch (e) {
        const err = e as { code?: string; message?: string };
        if (opts.mode !== 'preview') {
          recordRun({
            ...historyItem,
            durationMs: Date.now() - startedAt,
            ok: false,
            error: {
              code: err.code ?? 'INTERNAL',
              message: err.message ?? String(e),
            },
          });
        }
        throw e;
      }
    },
    [serial, pkg, recordRun]
  );

  /**
   * Imperative handle on the currently-mounted {@link CmdRunCard}. Used
   * by {@link onPanelRunCmd} so an inline ▷ click on a panel's command
   * list dispatches THROUGH the run card — meaning the result lands in
   * the card's "运行结果" pane (the user-visible affordance) instead
   * of being thrown away after recording history. Without this the
   * inline ▷ would silently succeed (history shows ok) but the user
   * sees no feedback in the middle column.
   */
  const cardRef = useRef<CmdRunCardHandle>(null);

  /**
   * Cross-package inline-run queue. When the user clicks ▷ on a NON-
   * active panel we have to (1) switch active pkg, (2) wait for the
   * new pkg's catalog to load, (3) wait for the new {@link CmdRunCard}
   * instance to mount on the picked command, then (4) fire the card's
   * imperative `run()`. The ref records "what to run once everything
   * lines up"; the effect below consumes it when {@code activeSpec}
   * settles on the matching (pkg, cmd) pair.
   */
  const pendingRunRef = useRef<{ pkg: string; cmd: string } | null>(null);

  /**
   * Inline ▷ run from a panel's command list. The panel passes its own
   * pkg up so we can route the run correctly even when the user
   * clicks on a not-yet-active app.
   *
   * <p>Flow:
   * <ul>
   *   <li>{@link pickCmdFor} sets the active pkg + active cmd (and
   *       expands the panel if needed).</li>
   *   <li>Subscribe / required-args specs short-circuit — those need
   *       user input via the form before they can be dispatched.</li>
   *   <li>If the run card is ALREADY mounted on this (pkg, spec), just
   *       fire its imperative {@code run()}. The card uses whatever
   *       args its form currently holds — which for a fresh select is
   *       the spec's defaults, so a zero-required-args command "just
   *       runs".</li>
   *   <li>Otherwise queue a {@link pendingRunRef}; the {@link useEffect}
   *       below fires {@code run()} once {@code activeSpec} updates
   *       to the matching spec (i.e. after the cross-pkg catalog fetch
   *       lands and the card remounts).</li>
   * </ul>
   */
  const onPanelRunCmd = useCallback(
    (panelPkg: string, spec: CommandSpec): void => {
      pickCmdFor(panelPkg, spec.name);
      if (spec.subscribe) return;
      if (specHasRequiredArgs(spec)) return;
      if (panelPkg === pkg && active === spec.name && cardRef.current) {
        void cardRef.current.run();
        return;
      }
      pendingRunRef.current = { pkg: panelPkg, cmd: spec.name };
    },
    [pkg, active, pickCmdFor]
  );

  /**
   * "Connected" semantics for a given panel:
   *
   * <ul>
   *   <li>Non-active panels: `true` by definition — the only way a
   *       client appears in {@code clientsQ.data} is if its `hello`
   *       frame is alive. We have no per-panel close-frame tracking
   *       because the events WS only listens to the active pkg.</li>
   *   <li>Active panel: factor in the most recent `open`/`hello`/`close`
   *       frame seen on the events WS — preserves the per-app red-dot
   *       semantic the old single-card layout exposed.</li>
   * </ul>
   */
  const connectedForPanel = useCallback(
    (c: ControlClientInfo): boolean => {
      if (c.package !== pkg) return true;
      return (
        !!clients.find((x) => x.package === pkg) &&
        lastHelloOrClose?.kind !== 'close'
      );
    },
    [pkg, clients, lastHelloOrClose]
  );

  /**
   * Render order for the left-rail panels: active pkg first, others in
   * the order {@code clientsQ} returned them (which is the hub's
   * connection order). Keeps the green-highlighted active panel pinned
   * to the top of the column so it's always visible above the fold,
   * even with many connected apps. `key={c.package}` on the panel
   * preserves React identity across order changes — no re-mount.
   */

  /**
   * Session memory — every {@link ControlClientInfo} we've EVER seen
   * during this page's lifetime, keyed by package. Survives a kill →
   * reconnect cycle so a panel never vanishes just because the live
   * `clients` snapshot temporarily lost the row.
   *
   * Critical for hello-only apps that don't carry a manifest marker
   * (e.g. older SDK builds like BlockJuggle's 0.1.0-SNAPSHOT): once
   * they disconnect they're absent from both `clients` AND
   * `installedApps`, so without session memory the panel would just
   * disappear with no Restart affordance.
   *
   * Ref-based (not state) because mutations don't need to trigger
   * re-renders on their own — the parent re-renders on `clients`
   * change anyway, at which point we consult the ref.
   */
  const seenClientsRef = useRef<Map<string, ControlClientInfo>>(new Map());
  useEffect(() => {
    if (clients.length === 0) return;
    const m = seenClientsRef.current;
    for (const c of clients) m.set(c.package, c);
  }, [clients]);

  /**
   * Unified left-rail entry list. One stable row per package so React
   * keeps the same `ControlClientPanel` instance across connected ↔
   * disconnected transitions (no flash on kill, no card-position
   * jumping on reconnect). Sources are merged in priority order:
   *
   *  1. live `clients` — drives the connected branch
   *  2. `installedApps` marker scan — drives the disconnected branch
   *     for apps that have the SDK marker baked into AndroidManifest
   *  3. session-remembered `ControlClientInfo` — drives the
   *     disconnected branch for hello-only apps that kill before
   *     they'd otherwise vanish from the rail
   */
  type SidebarEntry = {
    pkg: string;
    client?: ControlClientInfo;
    installed?: InstalledWshubApp;
  };
  const sidebarEntries = useMemo<SidebarEntry[]>(() => {
    const map = new Map<string, SidebarEntry>();

    for (const c of clients) {
      map.set(c.package, { pkg: c.package, client: c });
    }
    for (const a of installedApps) {
      const ex = map.get(a.package);
      if (ex) ex.installed = a;
      else map.set(a.package, { pkg: a.package, installed: a });
    }
    for (const [p, c] of seenClientsRef.current) {
      if (map.has(p)) continue;
      // Synthesize an `installed` shape from the last-known hello so
      // the disconnected panel has display name (via `sdkVersion`) +
      // a sensible `lastSeenAt`. detection='hello' marks that this
      // came from session memory, not a marker scan.
      map.set(p, {
        pkg: p,
        installed: {
          package: p,
          sdkVersion: c.app.version,
          detection: 'hello',
          isConnected: false,
          lastSeenAt: c.lastSeen,
        },
      });
    }

    // Active pkg first (if connected) then connected ones (most-recent
    // hello first via `connectedAt` desc), then disconnected ones
    // alphabetically — keeps the rail readable as the count grows.
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      const aLive = !!a.client;
      const bLive = !!b.client;
      if (a.pkg === pkg && aLive) return -1;
      if (b.pkg === pkg && bLive) return 1;
      if (aLive && !bLive) return -1;
      if (!aLive && bLive) return 1;
      if (aLive && bLive) {
        return (b.client?.connectedAt ?? 0) - (a.client?.connectedAt ?? 0);
      }
      return a.pkg.localeCompare(b.pkg);
    });
    return arr;
  }, [clients, installedApps, pkg]);

  const railIsEmpty = sidebarEntries.length === 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold tracking-tight">WS Commands</h1>
          <span
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
              clients.length > 0
                ? 'border-emerald-500/40 text-emerald-500'
                : 'border-line text-ink-muted'
            )}
            title={
              clients.length > 0
                ? clients.map((c) => `${c.app.name} ${c.app.version} (${c.package})`).join('\n')
                : '没有 app 在该设备上连接到 WS'
            }
          >
            <Layers className="h-3 w-3" />
            已连接 {clients.length} 个 app
          </span>
          {/*
           * Manual rescan button. The hub auto-polls `/control/clients`
           * every 3s and `/control/installed-apps` every 30s, but the
           * second one runs a real adb fan-out (per-package `dumpsys`
           * + APK manifest parse) that the user wants on-demand after
           * installing/launching a new SDK app —  no point waiting
           * for the next 30s tick. Spins both queries' refetch in
           * parallel; the icon itself spins while either is in flight
           * to give a single visual signal.
           */}
          <button
            type="button"
            disabled={!serial || clientsQ.isFetching || installedAppsQ.isFetching}
            onClick={() => {
              void clientsQ.refetch();
              void installedAppsQ.refetch();
            }}
            className={clsx(
              'inline-flex h-6 items-center gap-1 rounded-full border px-2 text-[11px] transition',
              !serial || clientsQ.isFetching || installedAppsQ.isFetching
                ? 'cursor-not-allowed border-line text-ink-dim'
                : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent'
            )}
            title="重新扫描该设备上接入 wshub SDK 的 app（重新读 /control/clients + /control/installed-apps）"
            aria-label="重新扫描"
          >
            <RefreshCw
              className={clsx(
                'h-3 w-3',
                (clientsQ.isFetching || installedAppsQ.isFetching) && 'animate-spin'
              )}
            />
            重扫
          </button>
          {/*
           * Browser-side events WS health. Separate from the per-panel
           * "connected" dot on each {@link ControlClientPanel} — this
           * pill says "can the SPA hear the hub", those dots say "is
           * the SDK talking to the hub". Helpful to disambiguate where
           * a hang is (network blip vs. device unplug).
           */}
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]',
              wsState === 'open'
                ? 'border-emerald-500/40 text-emerald-500'
                : wsState === 'connecting'
                  ? 'border-amber-500/50 text-amber-500'
                  : wsState === 'absent'
                    ? 'border-rose-500/50 text-rose-500'
                    : 'border-line text-ink-muted'
            )}
            title={
              wsState === 'open'
                ? '事件 WS 连接正常'
                : wsState === 'connecting'
                  ? '事件 WS 正在重连…（指数退避，最长 30s；接 4503 时遵循服务端 retryAfterMs 提示）'
                  : wsState === 'absent'
                    ? 'Hub 报告设备不存在（adb device not found）。已暂停重连，等待设备回连或点击"重连"。'
                    : '事件 WS 已断开'
            }
          >
            <span
              className={clsx(
                'h-1.5 w-1.5 rounded-full',
                wsState === 'open'
                  ? 'bg-emerald-500'
                  : wsState === 'connecting'
                    ? 'bg-amber-500 animate-pulse'
                    : wsState === 'absent'
                      ? 'bg-rose-500'
                      : 'bg-ink-dim'
              )}
            />
            {wsState === 'open'
              ? 'WS 已连接'
              : wsState === 'connecting'
                ? '重连中'
                : wsState === 'absent'
                  ? '设备离线'
                  : 'WS 离线'}
          </span>
        </div>
      </header>

      {serial && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1.4fr_1fr]">
          {/*
           * Left rail — one collapsible panel per connected app. Each
           * panel owns its own catalog query (keyed on serial + pkg)
           * and surfaces a per-app reconnect button. Click any command
           * inside any panel → that pkg becomes the active one; the
           * middle column's run card + the right column's events feed
           * follow.
           */}
          <aside className="max-h-[calc(100vh-160px)] space-y-2 overflow-y-auto pr-1">
            {/*
             * Unified "apps with SDK on this device" rail — one stable
             * card per package across connected / disconnected
             * transitions. `ControlClientPanel` switches its inner
             * layout in-place (catalog + 激活/重连 when live; Restart +
             * info when dead) so kill→reconnect never flashes the
             * panel away. Sources: live clients ∪ marker scan ∪
             * session-memory of every hello we've seen on this page.
             */}
            {railIsEmpty ? (
              installedAppsQ.isLoading || clientsQ.isLoading ? (
                /*
                 * Initial-load skeleton — the installed-apps scan runs
                 * a real adb fan-out (per-package dumpsys + APK
                 * manifest parse) that can take a few seconds on
                 * devices with hundreds of packages. Without this,
                 * the page momentarily flashes the "no app" empty
                 * state before the first response lands, which makes
                 * users think their app isn't integrated when in
                 * fact the scan just hasn't finished yet.
                 */
                <div className="space-y-2">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="card flex items-center gap-2 px-2 py-2"
                      aria-hidden
                    >
                      <span className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-ink-dim/40" />
                      <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-ink-dim/20" />
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="h-3 w-2/3 animate-pulse rounded bg-ink-dim/20" />
                        <div className="h-2.5 w-4/5 animate-pulse rounded bg-ink-dim/10" />
                      </div>
                      <div className="h-7 w-14 shrink-0 animate-pulse rounded bg-ink-dim/15" />
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 px-2 text-[11px] text-ink-dim">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    正在扫描该设备上接入 wshub SDK 的 app…
                  </div>
                </div>
              ) : (
                <div className="card-pad space-y-1 text-xs text-ink-muted">
                  <p>
                    没有检测到接入 wshub SDK 的 app（既无 manifest marker，也无 WS hello）。
                  </p>
                  <p>
                    请在 app 内确认 WsHub 已启动（参见
                    docs/ws-app-contrl/08-rn-bridge.md）。app
                    一安装/连接，这里会出现可折叠面板，或点上方"重扫"立即刷新。
                  </p>
                </div>
              )
            ) : (
              sidebarEntries.map((e) => (
                <ControlClientPanel
                  key={e.pkg}
                  serial={serial}
                  pkg={e.pkg}
                  client={e.client}
                  installed={e.installed}
                  isActive={e.pkg === pkg}
                  connected={e.client ? connectedForPanel(e.client) : false}
                  expanded={expandedSet.has(e.pkg)}
                  selectedCmdKey={e.pkg === pkg ? active : undefined}
                  onToggleExpanded={() => toggleExpanded(e.pkg)}
                  onPickCmd={(name) => pickCmdFor(e.pkg, name)}
                  onRunCmd={(spec) => onPanelRunCmd(e.pkg, spec)}
                  onReconnect={() => reconnectFor(e.pkg)}
                  onRestart={() => restartFor(e.pkg)}
                  busy={wsState === 'connecting' && e.pkg === pkg}
                />
              ))
            )}
          </aside>
          <section className="min-w-0">
            {activeSpec ? (
              <CmdRunCard
                ref={cardRef}
                key={`${activeSpec.name}:${
                  !replayArgsConsumedRef.current && replayArgs !== undefined
                    ? Date.now()
                    : 'idle'
                }`}
                spec={
                  !replayArgsConsumedRef.current && replayArgs !== undefined
                    ? {
                        ...activeSpec,
                        args: { ...activeSpec.args, example: replayArgs },
                      }
                    : activeSpec
                }
                onRun={async (args) => {
                  replayArgsConsumedRef.current = true;
                  const reply = (await runCommand(activeSpec.name, args)) as {
                    ok: boolean;
                    result?: unknown;
                    error?: { code: string; message: string };
                  };
                  // Unwrap the reply envelope so "运行结果" only renders
                  // the device handler's return payload — `ok` / `error`
                  // are surfaced via the card's error banner. Throwing
                  // on `!ok` keeps the failure path consistent with the
                  // device-throws-an-exception case.
                  if (!reply.ok) {
                    throw new Error(
                      reply.error?.message ??
                        `命令失败 (${reply.error?.code ?? 'INTERNAL'})`
                    );
                  }
                  return reply.result;
                }}
                onSnapshot={
                  activeSpec.hasSnapshot
                    ? async (args) => {
                        const reply = (await runCommand(activeSpec.name, args, {
                          mode: 'preview',
                        })) as {
                          ok: boolean;
                          result?: unknown;
                          error?: { code: string; message: string };
                        };
                        if (!reply.ok) {
                          throw new Error(
                            reply.error?.message ?? '加载当前值失败'
                          );
                        }
                        const r = reply.result;
                        return r && typeof r === 'object'
                          ? (r as Record<string, unknown>)
                          : undefined;
                      }
                    : undefined
                }
                satisfiedDeps={satisfiedDeps}
                onRunDep={async (depName) => {
                  const depSpec = commandsByName.get(depName);
                  if (!depSpec) return;
                  try {
                    await runCommand(depName, {});
                  } catch {
                    /* error already recorded in history */
                  }
                }}
                resolveDep={(depName) => commandsByName.get(depName)}
                onSubscribe={
                  activeSpec.subscribe && serial && pkg
                    ? async (args) => {
                        const r = await controlApi.sub({
                          serial,
                          package: pkg,
                          name: activeSpec.name,
                          args,
                        });
                        return r.subId;
                      }
                    : undefined
                }
                onUnsubscribe={
                  activeSpec.subscribe
                    ? async (id) => {
                        await controlApi.unsub({ subId: id });
                      }
                    : undefined
                }
                subEvents={subEvents}
              />
            ) : (
              <div className="card-pad text-sm text-ink-muted">
                从左侧选择一个命令。Catalog 通过设备 WS 适配器实时获取，每条命令的入参 /
                结果 schema 都来自 SDK 反射的 POJO 类型。
              </div>
            )}
          </section>
          <section className="min-w-0">
            <div className="card-pad min-w-0">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="inline-flex overflow-hidden rounded-md border border-line text-[11px]">
                  <button
                    className={clsx(
                      'inline-flex items-center gap-1 px-2 py-1 transition',
                      rightTab === 'events'
                        ? 'bg-accent-soft text-accent'
                        : 'text-ink-muted hover:bg-bg-soft hover:text-ink'
                    )}
                    onClick={() => setRightTab('events')}
                  >
                    <Activity className="h-3 w-3" /> Events
                  </button>
                  <button
                    className={clsx(
                      'inline-flex items-center gap-1 px-2 py-1 transition',
                      rightTab === 'history'
                        ? 'bg-accent-soft text-accent'
                        : 'text-ink-muted hover:bg-bg-soft hover:text-ink'
                    )}
                    onClick={() => setRightTab('history')}
                  >
                    <History className="h-3 w-3" /> History
                    {history.length > 0 && (
                      <span className="ml-1 rounded-full bg-bg-soft px-1 text-[10px] text-ink-muted">
                        {history.length}
                      </span>
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {inFlightId && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded border border-rose-500/40 px-1.5 py-0.5 text-[11px] text-rose-500 hover:bg-rose-500/10"
                      onClick={cancelInflight}
                      title={`Cancel cmd #${inFlightId.slice(0, 8)}`}
                    >
                      <XCircle className="h-3 w-3" /> 取消
                    </button>
                  )}
                  {rightTab === 'events' && (
                    <button className="btn-ghost" onClick={() => setEvents([])}>
                      Clear
                    </button>
                  )}
                  {rightTab === 'history' && history.length > 0 && (
                    <button
                      className="btn-ghost"
                      title="清空当前 (设备 × app) 的命令历史"
                      onClick={() => {
                        if (serial && pkg) updatePkgPrefs({ control: { history: [] } });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {rightTab === 'events' && (
                <ul className="max-h-[calc(100vh-260px)] min-w-0 space-y-1 overflow-auto">
                  {events.map((e, i) => (
                    <EventRow key={i} event={e} />
                  ))}
                  {events.length === 0 && (
                    <li className="rounded border border-dashed border-line p-2 text-center text-[11px] text-ink-muted">
                      No events yet.
                    </li>
                  )}
                </ul>
              )}

              {rightTab === 'history' && (
                <ul className="max-h-[calc(100vh-260px)] min-w-0 space-y-1 overflow-auto">
                  {history.map((h) => (
                    <li
                      key={h.id}
                      className="min-w-0 cursor-pointer rounded border border-line p-2 text-[11px] hover:border-accent"
                      title="点击：选中该命令并预填参数（再次点击 Run 即可重放）"
                      onClick={() => {
                        setActive(h.name);
                        setReplayArgs(h.args);
                        replayArgsConsumedRef.current = false;
                      }}
                    >
                      <div className="flex items-center justify-between text-ink-muted">
                        <code className={clsx('font-mono', h.ok === false ? 'text-rose-500' : 'text-accent')}>
                          {h.name}
                        </code>
                        <span>{tsLocal(new Date(h.startedAt).toISOString())}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[10px] text-ink-dim">
                        {h.durationMs !== undefined && <span>{h.durationMs}ms</span>}
                        {h.ok === true && <span className="text-emerald-500">ok</span>}
                        {h.ok === false && (
                          <span className="text-rose-500">
                            {h.error?.code ?? 'err'}
                          </span>
                        )}
                      </div>
                      {h.args !== undefined && h.args !== null && (
                        <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-soft p-1 font-mono text-[10px]">
                          {JSON.stringify(h.args, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                  {history.length === 0 && (
                    <li className="rounded border border-dashed border-line p-2 text-center text-[11px] text-ink-muted">
                      没有历史记录。运行一个命令后会出现在这里，点击即可重放。
                    </li>
                  )}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function kindClass(k: string): string {
  switch (k) {
    case 'open':
    case 'hello':
      return 'text-emerald-500';
    case 'close':
      return 'text-rose-500';
    case 'reply':
      return 'text-accent';
    case 'progress':
      return 'text-amber-500';
    case 'evt':
      return 'text-sky-500';
    default:
      return 'text-ink';
  }
}

/**
 * Build a short single-line summary of an event for the collapsed
 * default state of the events panel — designed so the user can scan
 * recent activity at a glance instead of wading through JSON dumps.
 *
 * Tries to label by command name (replies + progress carry `name` from
 * the hub-side enrichment in {@code ControlServer.handleReply}); falls
 * back to a payload preview otherwise.
 */
function summarizeEvent(e: ControlEvent): { label: string; suffix?: string } {
  if (e.kind === 'open') return { label: '连接已建立' };
  if (e.kind === 'close') return { label: '连接已断开' };
  if (e.kind === 'hello') {
    const h = e.payload as {
      app?: { name?: string; version?: string };
      capabilities?: unknown[];
    } | null;
    const appName = h?.app?.name ?? '?';
    const ver = h?.app?.version ?? '';
    const caps = Array.isArray(h?.capabilities) ? h!.capabilities!.length : 0;
    return { label: `hello · ${appName} ${ver}`.trim(), suffix: `${caps} cmds` };
  }
  if (e.kind === 'reply') {
    const r = e.payload as {
      id?: string;
      name?: string;
      ok?: boolean;
      error?: { code?: string };
    } | null;
    const name = r?.name ?? (r?.id ? `#${r.id.slice(0, 8)}` : '?');
    if (r?.ok) return { label: `reply ← ${name}`, suffix: 'ok' };
    return {
      label: `reply ← ${name}`,
      suffix: r?.error?.code ?? 'err',
    };
  }
  if (e.kind === 'evt') {
    const evtPayload = e.payload as { subId?: string } | null;
    const sid = evtPayload?.subId ? `#${evtPayload.subId.slice(0, 8)}` : '';
    return { label: `evt ${sid}`.trim() };
  }
  if (e.kind === 'progress') {
    const p = e.payload as { id?: string; name?: string } | null;
    const name = p?.name ?? (p?.id ? `#${p.id.slice(0, 8)}` : '?');
    return { label: `progress · ${name}` };
  }
  return { label: e.kind };
}

/**
 * One row of the events panel — single-line summary by default,
 * click-to-expand for the full JSON payload. Self-contained state keeps
 * the parent list re-renders cheap when frames pour in fast (e.g.
 * subscriptions emitting at 30 Hz).
 */
function EventRow({ event }: { event: ControlEvent }) {
  const [expanded, setExpanded] = useState(false);
  const { label, suffix } = useMemo(() => summarizeEvent(event), [event]);
  return (
    <li className="min-w-0 rounded border border-line text-[11px]">
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-2 px-2 py-1 text-left transition hover:bg-bg-soft"
        onClick={() => setExpanded((v) => !v)}
        title={expanded ? '折叠' : '展开 JSON'}
      >
        <span className={clsx('shrink-0 font-mono', kindClass(event.kind))}>
          {expanded ? '▾' : '▸'}
        </span>
        <span className="min-w-0 flex-1 truncate text-ink">{label}</span>
        {suffix && (
          <span className="shrink-0 rounded bg-bg-soft px-1 text-[10px] text-ink-dim">
            {suffix}
          </span>
        )}
        <span className="shrink-0 text-ink-dim">
          {tsLocal(new Date(event.time).toISOString())}
        </span>
      </button>
      {expanded && event.payload !== null && event.payload !== undefined && (
        <pre className="mx-2 mb-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-soft p-1 font-mono text-[10px]">
          {JSON.stringify(event.payload, null, 2)}
        </pre>
      )}
    </li>
  );
}
